'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Lenis from 'lenis';
import { useLanguage } from '@/i18n/LanguageProvider';
import { CookieConsent } from '@/app/v2/CookieConsent';
// Theme-agnostic helpers reused from v2 (single source). Imported for local use…
import { formatDate, venueShort, CountUp, L, useV2Lang } from '@/app/v2/_layout';

/**
 * Shared chrome for the v3 site — a wolverineworldwide.com-style redesign.
 *
 * Direction (per redesign brief, take 2): stark black & white, ultra-tight
 * heavy grotesk display (Clash Display ≈ ABC Diatype), Lenis smooth/inertia
 * scroll, a scattered image "galaxy" with scroll parallax, pill buttons with a
 * circular arrow, a floating nav. Crimson is kept ONLY for the signature
 * period/dot. Sparse, huge-type, lots of whitespace.
 *
 * Color tokens live in globals.css under `.v3-root`. Theme-agnostic helpers
 * (formatDate, venueShort, CountUp, L) are reused from v2 — single source.
 */

// …and re-exported so v3 pages can import them from one place.
export { formatDate, venueShort, CountUp, L };
export { useV2Lang as useLang };

export type Lang = 'CN' | 'EN' | 'DE';

export const ACCENT      = '#d6342c';
export const INK         = '#050505';
export const INK_SOFT    = 'rgba(5, 5, 5, 0.66)';
export const INK_MUTE    = 'rgba(5, 5, 5, 0.45)';
export const LINE        = 'rgba(5, 5, 5, 0.12)';
export const LINE_SOFT   = 'rgba(5, 5, 5, 0.07)';
export const LINE_STRONG = 'rgba(5, 5, 5, 0.24)';
export const PAPER       = '#ffffff';
export const DARK        = '#000000';

const NAV_COPY: Record<Lang, { about: string; gravilens: string; qiaodong: string; events: string; contact: string }> = {
  CN: { about: '关于', gravilens: 'GraviLens', qiaodong: 'Qiaodong', events: '活动', contact: '联系' },
  EN: { about: 'About', gravilens: 'GraviLens', qiaodong: 'Qiaodong', events: 'Events', contact: 'Contact' },
  DE: { about: 'Über uns', gravilens: 'GraviLens', qiaodong: 'Qiaodong', events: 'Events', contact: 'Kontakt' },
};

const FOOTER_COPY: Record<Lang, {
  title: string; lede: string; formCta: string;
  labels: { email: string; address: string; social: string; status: string; donations: string };
  statusBody: string; statusSub: string; donateBody: string; donateCta: string;
  impressum: string; datenschutz: string; terms: string; rights: string;
}> = {
  CN: {
    title: '联系我们', lede: '巡演合作、艺人邀约、媒体咨询、一般问题——欢迎邮件联系，或使用联系表单。', formCta: '联系表单',
    labels: { email: '邮箱', address: '地址', social: '社交', status: '法律状态', donations: '捐赠' },
    statusBody: '已注册公益协会 (Eingetragener gemeinnütziger Verein)', statusSub: '慕尼黑 · 2025 年 4 月成立',
    donateBody: '可开具德国税务认可的公益捐赠收据。', donateCta: '支持 PathIN',
    impressum: '法律声明', datenschutz: '隐私政策', terms: '使用条款', rights: '保留所有权利',
  },
  EN: {
    title: 'Get in touch', lede: 'For booking, partnerships, press or general inquiries — write to the address below or use the contact form.', formCta: 'Contact form',
    labels: { email: 'Email', address: 'Address', social: 'Social', status: 'Status', donations: 'Donations' },
    statusBody: 'Eingetragener gemeinnütziger Verein (e.V.)', statusSub: 'Munich · Founded April 2025',
    donateBody: 'Tax-deductible donation receipts available on request.', donateCta: 'Support PathIN',
    impressum: 'Legal Notice', datenschutz: 'Privacy', terms: 'Terms', rights: 'All rights reserved',
  },
  DE: {
    title: 'Kontakt', lede: 'Für Booking, Kooperationen, Presse oder allgemeine Anfragen — schreiben Sie uns oder nutzen Sie das Kontaktformular.', formCta: 'Kontaktformular',
    labels: { email: 'E-Mail', address: 'Adresse', social: 'Social Media', status: 'Status', donations: 'Spenden' },
    statusBody: 'Eingetragener gemeinnütziger Verein (e.V.)', statusSub: 'München · Gegründet April 2025',
    donateBody: 'Steuerlich abzugsfähige Spendenbescheinigung auf Anfrage.', donateCta: 'PathIN unterstützen',
    impressum: 'Impressum', datenschutz: 'Datenschutz', terms: 'Nutzungsbedingungen', rights: 'Alle Rechte vorbehalten',
  },
};

const HOME = '/next';
const ABOUT = '/next/about';
const EVENTS = '/next/events';

/* ──────── Smooth scroll (Lenis) ──────── */
function useLenis() {
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    let raf = 0;
    const loop = (time: number) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
}

/* ──────── Layout ──────── */
export function V3Layout({ children, hideFooterContact }: { children: React.ReactNode; hideFooterContact?: boolean }) {
  useLenis();
  return (
    <div className="v3-root v3-canvas min-h-screen antialiased">
      <V3Nav />
      <main>{children}</main>
      <V3Footer hideContact={hideFooterContact} />
      <CookieConsent />
    </div>
  );
}

/* ──────── Nav (floating pill on scroll) ──────── */
export function V3Nav() {
  const { language, setLanguage } = useLanguage();
  const lang = (language ?? 'EN') as Lang;
  const c = NAV_COPY[lang];
  const [scrolled, setScrolled] = useState(false);
  // The smooth collapse (à la wolverine): keep the row space-between and animate
  // a NUMERIC max-width. Collapsed width is measured from the content so the
  // pill hugs it with one consistent gap — no width:auto snap.
  const [collapsedW, setCollapsedW] = useState(560);
  const logoRef = useRef<HTMLAnchorElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);
  const GAP = 44;   // gap between logo and the menu group when collapsed
  const PAD = 26;   // horizontal padding each side when collapsed

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const measure = () => {
      const lw = logoRef.current?.offsetWidth ?? 0;
      const rw = rightRef.current?.offsetWidth ?? 0;
      if (lw && rw) setCollapsedW(Math.round(lw + rw + GAP + PAD * 2));
    };
    measure();
    const t = window.setTimeout(measure, 450); // re-measure after webfont swap
    window.addEventListener('resize', measure);
    return () => { window.clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [lang]);

  return (
    <nav className="fixed inset-x-0 top-0 z-50 px-4 md:px-6 pt-4 flex justify-center">
      <div
        className="flex items-center justify-between w-full"
        style={{
          maxWidth: scrolled ? collapsedW : 1480,
          height: scrolled ? 46 : 50,
          paddingInline: scrolled ? `${PAD}px` : '0.25rem',
          borderRadius: 999,
          backgroundColor: scrolled ? 'rgba(5,5,5,0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          boxShadow: scrolled ? '0 18px 44px -28px rgba(0,0,0,0.7)' : 'none',
          transition:
            'max-width 0.55s cubic-bezier(0.215,0.61,0.355,1), padding 0.55s cubic-bezier(0.215,0.61,0.355,1), height 0.4s cubic-bezier(0.215,0.61,0.355,1), background-color 0.35s ease, box-shadow 0.4s ease',
        }}
      >
        <Link ref={logoRef} to={HOME} className="shrink-0 font-black tracking-[-0.02em] text-white text-[14px]">
          PathIN<span style={{ color: ACCENT }}>.</span>
        </Link>
        <div ref={rightRef} className="flex items-center gap-5 shrink-0">
          <div className="hidden lg:flex items-center gap-5 text-[12.5px] tracking-[-0.005em] font-medium text-white/80">
            <Link to={ABOUT} className="hover:text-white transition-colors">{c.about}</Link>
            <Link to="/GraviLens" className="hover:text-white transition-colors">{c.gravilens}</Link>
            <Link to="/Qiaodong" className="hover:text-white transition-colors">{c.qiaodong}</Link>
            <Link to={EVENTS} className="hover:text-white transition-colors">{c.events}</Link>
            <Link to="/kontakt" className="hover:text-white transition-colors">{c.contact}</Link>
          </div>
          <span className="hidden lg:block h-3 w-px" style={{ backgroundColor: 'rgba(255,255,255,0.22)' }} aria-hidden />
          <div className="flex items-center gap-2 text-[11px] font-semibold">
            {(['CN', 'EN', 'DE'] as Lang[]).map((Lc, i) => {
              const active = lang === Lc;
              return (
                <span key={Lc} className="flex items-center gap-2">
                  {i > 0 && <span className="text-white/25 font-normal">·</span>}
                  <button onClick={() => setLanguage(Lc)} className={`transition-colors ${active ? '' : 'text-white/50 hover:text-white'}`} style={active ? { color: ACCENT } : undefined}>
                    {Lc === 'CN' ? '中' : Lc}
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
}

/* ──────── Pill button (label + circular arrow) ──────── */
export function Pill({ to, href, children, dark, external }: { to?: string; href?: string; children: React.ReactNode; dark?: boolean; external?: boolean }) {
  const cls = `v3-pill${dark ? ' v3-pill--dark' : ''}`;
  const inner = (<><span>{children}</span><span className="v3-pill__arrow" aria-hidden>{external ? '↗' : '→'}</span></>);
  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  return <a href={href} className={cls} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>{inner}</a>;
}

/* ──────── Footer (B&W) ──────── */
export function V3Footer({ hideContact }: { hideContact?: boolean }) {
  const { language } = useLanguage();
  const lang = (language ?? 'EN') as Lang;
  const f = FOOTER_COPY[lang];

  return (
    <footer id="contact" className="px-6 md:px-10 pt-28 pb-12" style={{ borderTop: `1px solid ${LINE}` }}>
      <div className="mx-auto max-w-[1480px]">
        {!hideContact && (
          <div className="grid gap-12 md:grid-cols-12 items-start">
            <div className="md:col-span-6">
              <h4 className="v3-display-cjk text-[clamp(40px,6vw,92px)]">
                {f.title}<span style={{ color: ACCENT }}>.</span>
              </h4>
              <p className="mt-8 text-[16px] leading-[1.6] v3-soft max-w-[42ch]">{f.lede}</p>
              <div className="mt-10"><Pill to="/kontakt">{f.formCta}</Pill></div>
            </div>
            <div className="md:col-span-3 md:col-start-7 text-[14px] leading-[1.7] v3-soft">
              <p className="text-[11px] uppercase tracking-[0.14em] v3-mute mb-3 font-medium">{f.labels.email}</p>
              <p><a href="mailto:pathin@pathin.eu" className="hover:text-[#050505]">pathin@pathin.eu</a></p>
              <p><a href="mailto:gravilens@pathin.eu" className="hover:text-[#050505]">gravilens@pathin.eu</a></p>
              <p><a href="mailto:qiaodong@pathin.eu" className="hover:text-[#050505]">qiaodong@pathin.eu</a></p>
              <p className="text-[11px] uppercase tracking-[0.14em] v3-mute mt-8 mb-3 font-medium">{f.labels.address}</p>
              <p>PathIN e.V.<br/>Arabellastraße 5, Apt. 416<br/>81925 München, Deutschland</p>
            </div>
            <div className="md:col-span-3 text-[14px] leading-[1.7] v3-soft">
              <p className="text-[11px] uppercase tracking-[0.14em] v3-mute mb-3 font-medium">{f.labels.social}</p>
              <p><a href="https://instagram.com/pathin.official" target="_blank" rel="noopener noreferrer" className="hover:text-[#050505]">Instagram @pathin.official ↗</a></p>
              <p><a href="https://instagram.com/gravilens.music" target="_blank" rel="noopener noreferrer" className="hover:text-[#050505]">@gravilens.music ↗</a></p>
              <p className="text-[11px] uppercase tracking-[0.14em] v3-mute mt-8 mb-3 font-medium">{f.labels.status}</p>
              <p>{f.statusBody}</p>
              <p className="v3-mute mt-1 text-[12px]">{f.statusSub}</p>
              <Link to="/donate" className="inline-block mt-6 underline underline-offset-4 v3-ink hover:no-underline">{f.donateCta} →</Link>
            </div>
          </div>
        )}

        <div className={`${hideContact ? '' : 'mt-24'} pt-8 flex flex-col md:flex-row items-baseline justify-between gap-4 text-[12px] v3-mute uppercase tracking-wider border-t`} style={{ borderColor: LINE }}>
          <span>© 2026 PathIN e.V. · {f.rights}</span>
          <div className="flex gap-6">
            <Link to="/impressum" className="hover:text-[#050505]">{f.impressum}</Link>
            <Link to="/datenschutz" className="hover:text-[#050505]">{f.datenschutz}</Link>
            <Link to="/legal/terms" className="hover:text-[#050505]">{f.terms}</Link>
          </div>
        </div>

        <div className="mt-12 select-none">
          <p className="v3-display text-[clamp(80px,19vw,300px)]" style={{ color: 'rgba(5,5,5,0.045)', lineHeight: 0.9 }} aria-hidden>
            PathIN<span style={{ color: 'rgba(214,52,44,0.18)' }}>.</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ──────── Atoms ──────── */
export function Rule({ strong }: { strong?: boolean }) {
  return <div className="h-px w-full" style={{ backgroundColor: strong ? LINE_STRONG : LINE }} />;
}

export function Eyebrow({ children, accent, light }: { children: React.ReactNode; accent?: boolean; light?: boolean }) {
  const color = accent ? ACCENT : light ? 'rgba(255,255,255,0.7)' : INK_MUTE;
  return (
    <span className="inline-flex items-center gap-3 text-[11px] tracking-[0.16em] uppercase font-semibold" style={{ color }}>
      <span className="inline-block h-[1px] w-8" style={{ backgroundColor: color }} aria-hidden />
      {children}
    </span>
  );
}

export function Stat({ label, value, dark }: { label: string; value: string; dark?: boolean }) {
  const isNumeric = /^[\d.]+\D*$/.test(value);
  return (
    <div className="flex items-baseline justify-between border-t pt-3" style={{ borderColor: dark ? 'rgba(255,255,255,0.2)' : LINE }}>
      <span className="text-[12px] tracking-[0.14em] uppercase font-medium" style={{ color: dark ? 'rgba(255,255,255,0.6)' : INK_MUTE }}>{label}</span>
      <span className="v3-display text-[clamp(22px,1.8vw,30px)] tabular-nums" style={{ color: dark ? '#fff' : INK }}>
        {isNumeric ? <CountUp to={value} /> : value}
      </span>
    </div>
  );
}

/* ──────── Reveal (fade + rise) ──────── */
function useReveal<T extends HTMLElement>(delay = 0) {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { window.setTimeout(() => setShown(true), delay); obs.disconnect(); }
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, shown };
}

export function V3Reveal({ children, as: Tag = 'div', delay = 0, className = '', ...rest }: {
  children: React.ReactNode; as?: keyof React.JSX.IntrinsicElements; delay?: number; className?: string; [key: string]: unknown;
}) {
  const { ref, shown } = useReveal<HTMLElement>(delay);
  const Component = Tag as unknown as React.ComponentType<Record<string, unknown>>;
  return (
    <Component ref={ref as React.Ref<HTMLElement>} className={`v3-reveal${shown ? ' is-visible' : ''} ${className}`} {...rest}>
      {children}
    </Component>
  );
}

export function V3RevealSection(props: { children: React.ReactNode; delay?: number } & React.HTMLAttributes<HTMLElement>) {
  return <V3Reveal as="section" {...props} />;
}

/* ──────── KineticTitle (per-line mask wipe-up) ──────── */
export function KineticTitle({ text, className = '', light = false, accentLast = true, stagger = false }: { text: string; className?: string; light?: boolean; accentLast?: boolean; stagger?: boolean }) {
  const lines = text.split('\n');
  const lastIdx = lines.length - 1;
  const { ref, shown } = useReveal<HTMLHeadingElement>(0);
  return (
    <h2 ref={ref} className={className} style={light ? { color: '#fff' } : undefined}>
      {lines.map((line, i) => {
        const isLast = i === lastIdx;
        const mtc = line.match(/[。.?!？！]+$/);
        const trailing = mtc?.[0] ?? '';
        const body = trailing ? line.slice(0, -trailing.length) : line;
        const punct = trailing.replace(/。/g, '.').replace(/？/g, '?').replace(/！/g, '!');
        return (
          <span key={i} className={`v3-line-mask${shown ? ' is-visible' : ''}`} style={stagger ? { marginLeft: `${i * 1.6}em` } : undefined}>
            <span className="v3-line-inner" style={{ transitionDelay: `${i * 90}ms`, paddingBottom: '0.12em', marginBottom: '-0.12em' }}>
              {body}
              {punct && <span lang="en" style={accentLast && isLast ? { color: ACCENT } : undefined}>{punct}</span>}
            </span>
          </span>
        );
      })}
    </h2>
  );
}

/* ──────── ImageGalaxy ────────
 * Scattered thumbnails (wolverine "StandoutImageGalaxy") that parallax at
 * per-item speeds as the section crosses the viewport, and pop in (scale +
 * fade) on first entry. Positions are % of the container; size in px.
 * Decorative — hidden from a11y and on small screens by the caller. */
export type GalaxyItem = { src: string; x: string; y: string; size: number; speed: number; rotate?: number };

export function ImageGalaxy({ items, className = '' }: { items: GalaxyItem[]; className?: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const nodes = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      nodes.current.forEach((node, i) => {
        if (!node) return;
        const sp = items[i]?.speed ?? 0;
        const rot = items[i]?.rotate ?? 0;
        if (reduce) { node.style.transform = `rotate(${rot}deg)`; node.style.opacity = '1'; return; }
        // Wolverine model (measured from the real site): images move VERTICALLY
        // only; direction depends on position — those above the section's middle
        // slide up, those below slide down, faster the farther out. They converge
        // to their layout position when the section is centered, then disperse.
        const yFrac = parseFloat(items[i]?.y ?? '50') / 100;     // 0 (top) .. 1 (bottom)
        const dir = yFrac < 0.5 ? -1 : 1;                        // up for top half, down for bottom half
        const itemTop = rect.top + yFrac * rect.height;
        const prog = clamp((vh - rect.top) / (vh + rect.height), 0, 1); // 0 entering → 1 leaving
        const ty = (prog - 0.5) * sp * dir * 2.2;
        const enter = clamp((vh - itemTop) / (vh * 0.5), 0, 1);
        node.style.transform = `translateY(${ty.toFixed(1)}px) rotate(${rot}deg)`;
        node.style.opacity = enter.toFixed(3);
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); cancelAnimationFrame(raf); };
  }, [items]);

  return (
    <div ref={containerRef} className={`relative ${className}`} aria-hidden>
      {items.map((it, i) => (
        <div
          key={it.src + i}
          ref={(n) => { nodes.current[i] = n; }}
          className="v3-galaxy-img"
          style={{ left: it.x, top: it.y, width: it.size, height: it.size, opacity: 0, transition: 'transform 80ms linear, opacity 80ms linear' }}
        >
          <img src={it.src} alt="" loading="lazy" />
        </div>
      ))}
    </div>
  );
}
